// The company's brand colour on customer documents (quote/invoice PDF + email) — the ONE owner
// (operator 2026-10-21: "brand color on pdfs i thought colors were picked in settings?").
//
// Source: companies.theme_color, the field Settings writes (CompanyProfileSettingsTab,
// CompanyProfile/BrandingSection) and the portal BusinessPage already reads. Not the
// whitelabels colour: that is the PLATFORM brand (TradesMatePro / SMBMate), while a quote or
// invoice is the contractor's own document.
//
// The column defaults to '#3B82F6' (verified live 2026-10-21: 330 of 331 companies hold it),
// so that value means "never picked" and returns null — each document then keeps its existing
// look unchanged. A picked colour returns the three shades a document needs:
//   accent      fills and borders (table header, rules, bars)
//   onAccent    text ON the accent fill: white or black, whichever contrasts more (always >= 4.5:1)
//   accentText  the accent used as text on white, darkened until it reaches 4.5:1 (WCAG AA)
//   accentTextDark  the accent used as text on the dark portal quote page, lightened to 4.5:1

export const DEFAULT_THEME_COLOR = '#3B82F6';

const parseHex = (value) => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value || '').trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};

const toHex = (rgb) => `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;

const luminance = (rgb) => {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two RGB triples. */
const contrast = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];
const DARK_SURFACE = [30, 58, 138]; // Tailwind blue-900, see accentTextDark

// The three shades for one colour; text shades are contrast-checked (see the header comment).
const shadesFor = (rgb) => {
  const onAccent = contrast(WHITE, rgb) >= contrast(BLACK, rgb) ? '#ffffff' : '#000000';

  // Checked on the ROUNDED channels, so the printed hex itself meets 4.5:1.
  let text = rgb;
  for (let i = 0; i < 40 && contrast(text, WHITE) < 4.5; i += 1) {
    text = text.map((c) => Math.floor(c * 0.9));
  }
  // accentTextDark (2026-10-30, the customer portal quote page, which is always dark): the accent
  // as text on a dark surface, lightened toward white until it reaches 4.5:1 against blue-900
  // (#1e3a8a), the lightest shade of that page's header, so it also clears the darker slate-900.
  let dark = rgb;
  for (let i = 0; i < 40 && contrast(dark, DARK_SURFACE) < 4.5; i += 1) {
    dark = dark.map((c) => Math.ceil(c + (255 - c) * 0.1));
  }
  return { accent: toHex(rgb), onAccent, accentText: toHex(text), accentTextDark: toHex(dark) };
};

const pickedColor = (value, columnDefault) => {
  const rgb = parseHex(value);
  if (!rgb) return null;
  if (toHex(rgb) === toHex(parseHex(columnDefault))) return null;
  return rgb;
};

/**
 * @param {string|null} themeColor  companies.theme_color
 * @returns {{accent: string, onAccent: string, accentText: string} | null}  null = use the default look
 */
export function documentBrandColor(themeColor) {
  const rgb = pickedColor(themeColor, DEFAULT_THEME_COLOR);
  return rgb ? shadesFor(rgb) : null;
}

// companies.secondary_color — the SECOND brand shade (operator ruling 2026-10-29: "make the
// secondary color do something to match other companies that use a secondary"). Jobber pairs a
// main brand colour with an accent colour; ServiceTitan's document templates colour hyperlinks
// and accent blocks separately from the header. So on customer documents the primary colour keeps
// the header, title, table header and totals, and the secondary colour takes the call-to-action
// button, links, and the accent rules/side bars.
//
// The column defaults to '#6B7280' (verified live 2026-10-29: 331 of 333 companies hold it), so
// that value means "never picked" and returns null — each surface then uses exactly what it used
// before (its primary-colour or stock shade), so a company without a secondary sees no change.
export const DEFAULT_SECONDARY_COLOR = '#6B7280';

/**
 * @param {string|null} secondaryColor  companies.secondary_color
 * @returns {{accent: string, onAccent: string, accentText: string} | null}  null = fall back to the current look
 */
export function documentSecondaryColor(secondaryColor) {
  const rgb = pickedColor(secondaryColor, DEFAULT_SECONDARY_COLOR);
  return rgb ? shadesFor(rgb) : null;
}
